import React, { useState, useContext, useReducer } from "react"
import Popover from "@material-ui/core/Popover"
import Dialog from "@material-ui/core/Dialog"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import CircularProgress from "@material-ui/core/CircularProgress"
import { useHistory } from "react-router-dom"
import axios from "axios"

import AuthContext from "../../../../context/auth-context"
import { setAllowChanges, getProperMetadata } from "../../utils/utils"
import { decisionReducer } from "../../../Utils/requestUtils"
import ConfirmationContent from "./ConfirmationDialog/ConfirmationContent"

const COLORS = {
    positive: { border: "#27ae60", text: "#27ae60", bg: "#f0faf4" },
    negative:  { border: "#e74c3c", text: "#e74c3c", bg: "#fdf3f2" },
    neutral:   { border: "#95a5a6", text: "#7f8c8d", bg: "#f8f9fa" },
}

function flagToVariant(flag) {
    if (flag === "positive") return "positive"
    if (flag === "negative") return "negative"
    return "neutral"
}

function IndicatorCard({ title, variant = "neutral", label, description, score }) {
    const c = COLORS[variant] || COLORS.neutral
    return (
        <div style={{
            borderLeft: `4px solid ${c.border}`,
            background: c.bg,
            borderRadius: "6px",
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            flex: 1,
        }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280" }}>
                {title}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>
                {label}
            </div>
            {description &&
                <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.4 }}>
                    {description}
                </div>
            }
            {score != null &&
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Similaridade: <span style={{ fontWeight: 600, color: "#374151" }}>{score.toFixed(2).replace(".", ",")}%</span>
                </div>
            }
        </div>
    )
}

// phase: "confirming" → popover | "submitting" | "done" → dialog
function ConfirmPopover({ anchorEl, manualAnalysisStatus, event_key, onCancel }) {
    const history = useHistory()
    const [phase, setPhase] = useState("confirming")
    const [resultState, dispatchResult] = useReducer(
        decisionReducer,
        { isLoading: false, isError: false, finished: false }
    )

    const isApprove = manualAnalysisStatus === "manually_approved"
    const title = isApprove ? "Aprovar" : "Reprovar"
    const confirmClass = ["button", isApprove ? "approved" : "reproved", "normalText", "normalMediumSize"].join(" ")

    const handleConfirm = () => {
        setPhase("submitting")
        dispatchResult({ type: "send_request_init" })
        axios.put('/dash/account_event/face_database_validation/' + event_key, {
            analysis_status: manualAnalysisStatus
        }).then(() => {
            dispatchResult({ type: "send_request_success" })
            setPhase("done")
        }).catch(error => {
            if ((error.response || {}).status === 403) dispatchResult({ type: "send_request_failure_403" })
            else if ((error.response || {}).status === 409) dispatchResult({ type: "send_request_failure_409" })
            else dispatchResult({ type: "send_request_failure" })
            setPhase("done")
        })
    }

    return (
        <>
            <Popover
                open={phase === "confirming" && Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onCancel}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                PaperProps={{ style: { padding: "20px 24px", borderRadius: 8, minWidth: 220 } }}
            >
                <div className={["blueText", "normalMediumSize"].join(" ")} style={{ marginBottom: 16, fontWeight: 600 }}>
                    Confirmar: {title}?
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <div className={confirmClass} onClick={handleConfirm}>
                        {title}
                    </div>
                    <div
                        className={["button", "cancel", "normalText", "normalMediumSize"].join(" ")}
                        onClick={onCancel}
                    >
                        Cancelar
                    </div>
                </div>
            </Popover>

            <Dialog
                open={phase === "submitting" || phase === "done"}
                onClose={() => history.push("/face-database-validation-inbox")}
                keepMounted={false}
                disableBackdropClick={phase === "submitting"}
                disableEscapeKeyDown={phase === "submitting"}
            >
                <DialogTitle style={{ paddingBottom: 0, width: 500 }} className={["blueText", "normalMediumSize"].join(" ")}>
                    {phase === "done" ? "Confirmação de Revisão" : title}
                </DialogTitle>
                <DialogContent>
                    {phase === "submitting" ? (
                        <div style={{ display: "flex", width: 310, height: 217, margin: "auto" }}>
                            <CircularProgress style={{ margin: "auto" }} />
                        </div>
                    ) : (
                        <ConfirmationContent
                            onClose={() => history.push("/face-database-validation-inbox")}
                            data={{ message: resultState.message, success: !resultState.isError }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

function Observation(props) {
    const [anchorEl, setAnchorEl] = useState(null)
    const [manualAnalysisStatus, setManualAnalysisStatus] = useState(null)

    const roles = useContext(AuthContext).user_data.roles
    const allowChanges = setAllowChanges(props.event.analysis_status, roles, "fdv")

    const dm = getProperMetadata(props.event.analysis_status_events)
    const faceMatch = dm?.face_match
    const dbMatch   = dm?.validator_face_match_on_database
    const fraudFace = dm?.fraud_face

    const faceMatchVariant = faceMatch?.flag ? flagToVariant(faceMatch.flag) : null
    const dbMatchVariant = dbMatch
        ? dbMatch.face_match_available
            ? dbMatch.recommendation === "approve" ? "positive" : "negative"
            : "neutral"
        : null

    const hasIndicators = faceMatch || dbMatch || fraudFace

    const handleOpen = (e, status) => {
        setManualAnalysisStatus(status)
        setAnchorEl(e.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
        setTimeout(() => setManualAnalysisStatus(null), 300)
    }

    return (
        <div className="analysisCard">
            <span className={["blueText", "subtitleSize"].join(" ")}>Revisão</span>

            {hasIndicators &&
                <div style={{ display: "flex", flexDirection: "row", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                    {faceMatch && faceMatchVariant &&
                        <IndicatorCard
                            title="Face Match"
                            variant={faceMatchVariant}
                            label={
                                faceMatchVariant === "positive"
                                    ? "Selfie corresponde à foto do documento"
                                    : faceMatchVariant === "negative"
                                        ? "Selfie não corresponde à foto do documento"
                                        : "Inconclusivo"
                            }
                            description={faceMatch.description}
                            score={faceMatch.score ?? null}
                        />
                    }
                    {dbMatch && dbMatchVariant &&
                        <IndicatorCard
                            title="Validação em Base de Rostos"
                            variant={dbMatchVariant}
                            label={
                                !dbMatch.face_match_available ? "Face não encontrada na base"
                                : dbMatchVariant === "positive" ? "Correspondência confirmada"
                                : "Correspondência não confirmada"
                            }
                            description={
                                dbMatch.face_match_available
                                    ? "Face do CPF encontrada na base de rostos"
                                    : "Face do CPF fornecido não encontrada na base de rostos"
                            }
                            score={dbMatch.face_match_available ? dbMatch.face_match_score : null}
                        />
                    }
                    {fraudFace?.flag &&
                        <IndicatorCard
                            title="Prova de Vida"
                            variant={flagToVariant(fraudFace.flag)}
                            label={
                                flagToVariant(fraudFace.flag) === "positive" ? "Aprovada"
                                : flagToVariant(fraudFace.flag) === "negative" ? "Reprovada"
                                : "Inconclusivo"
                            }
                            description={fraudFace.description}
                        />
                    }
                </div>
            }

            {allowChanges &&
                <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 24 }}>
                    <div
                        className={["button", "approved", "normalText", "normalMediumSize"].join(" ")}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={(e) => handleOpen(e, "manually_approved")}
                    >
                        APROVAR
                    </div>
                    <div
                        className={["button", "reproved", "normalText", "normalMediumSize"].join(" ")}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={(e) => handleOpen(e, "manually_reproved")}
                    >
                        REPROVAR
                    </div>
                </div>
            }

            {allowChanges && manualAnalysisStatus &&
                <ConfirmPopover
                    anchorEl={anchorEl}
                    manualAnalysisStatus={manualAnalysisStatus}
                    event_key={props.event_key}
                    onCancel={handleClose}
                />
            }
        </div>
    )
}

export default Observation
